import type { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';
import CryptoJS from 'crypto-js';

import * as userApi from '@/services/userService';

import { history } from '@/lib/routeHistory';
import { getUserRefreshToken } from '@/services/authentication';
import { sendAnalytics } from '@/services/analyticsService';
import { getAdminTokenEncrypt } from '@/services/apiKeyService';
import { toast } from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
	ResetPassword,
	ResetPasswordError
} from './exceptions/resetPasswordError';

import * as actions from './userActions';
import type { User, UserAction } from './userTypes';

export function* loginUserSaga({ payload }: UserAction): SagaIterator {
	try {
		const data = yield call(
			userApi.login,
			(payload as User).email,
			(payload as User).password!
		);

		const { otpStateToken } = data.data;

		if (otpStateToken) {
			yield put(
				actions.userAddOtpStateToken({
					otpStateToken,
					email: (payload as User).email
				})
			);
			return;
		}

		const {
			data: {
				data: { accessToken, refreshToken, user }
			}
		} = yield call(
			userApi.login,
			(payload as User).email,
			(payload as User).password!
		);

		yield put(
			actions.loginUserSuccess({
				accessToken,
				refreshToken,
				...user
			})
		);

		const { onboarding, email, features } = user;

		const onboardingStatus = onboarding?.status;

		const isOnboardingIncomplete =
			onboardingStatus === 'pending' || onboardingStatus === 'skipped';

		if (isOnboardingIncomplete) {
			history.push('/onboarding');
			return;
		}

		sendAnalytics({
			type: 'identify',
			userId: email,
			traits: {
				TestGroup: features?.businessSortingTestVariant
			}
		});

		sendAnalytics({
			type: 'track',
			userId: email,
			eventName: 'User logged in'
		});
	} catch (error) {
		if (error instanceof AxiosError) {
			toast.error(error.response?.data.message);
		}

		yield put(actions.loginUserFailure(error));
	}
}

export function* userResetPasswordSaga({ payload }: UserAction): SagaIterator {
	try {
		const validationErrors: ResetPassword = {};
		const lowercase = /[a-z]/;
		const uppercase = /[A-Z]/;
		const digit = /[0-9]/;
		const specialChar = /[\W]/;

		const currentPassword = String(
			(payload as ResetPassword).currentPassword
		).trim();

		if (currentPassword.length === 0) {
			validationErrors.currentPassword =
				'Current password can not be empty.';
		}

		const newPassword = String(
			(payload as ResetPassword).newPassword
		).trim();

		if (newPassword.length === 0) {
			validationErrors.newPasswordEmpty =
				'New password can not be empty.';
		}

		if (newPassword.length < 6) {
			validationErrors.newPasswordLength =
				'New password must contain at least 6 characters.';
		}

		if (!lowercase.test(newPassword)) {
			validationErrors.newPasswordLower =
				'New password must contain at least one lowercase letter.';
		}

		if (!uppercase.test(newPassword)) {
			validationErrors.newPasswordUpper =
				'New password must contain at least one uppercase letter.';
		}

		if (!digit.test(newPassword)) {
			validationErrors.newPasswordNumber =
				'New password must contain at least one number.';
		}

		if (!specialChar.test(newPassword)) {
			validationErrors.newPasswordSpecial =
				'New password must contain at least one special character. (!,@,#,$,%,^,&,*)';
		}

		if (newPassword === currentPassword) {
			validationErrors.newPasswordSame =
				'New password must not be same as the current password.';
		}

		const retryNewPassword = String(
			(payload as ResetPassword).retryNewPassword
		).trim();

		const retryNewPasswordArray = [];
		if (retryNewPassword.trim().length === 0) {
			validationErrors.retryNewPasswordEmpty =
				'Repeat password can not be empty.';

			retryNewPasswordArray.push(validationErrors.retryNewPasswordEmpty);
		}

		if (retryNewPassword.trim() !== newPassword) {
			validationErrors.retryNewPasswordMatch =
				'Repeat password and new password must match.';

			retryNewPasswordArray.push(validationErrors.retryNewPasswordMatch);
		}

		if (retryNewPasswordArray.length > 0) {
			validationErrors.retryNewPassword =
				retryNewPasswordArray.join('\n');
		}

		if (Object.keys(validationErrors).length > 0) {
			const errorString = [];
			for (const [key, value] of Object.entries(validationErrors)) {
				if (
					key != 'retryNewPasswordMatch' &&
					key != 'retryNewPassword' &&
					key != 'retryNewPasswordEmpty' &&
					key != 'currentPassword'
				) {
					errorString.push(value);
				}
			}
			validationErrors.newPassword = errorString.join('\n');

			throw new ResetPasswordError(validationErrors);
		}

		const {
			data: { code, message, data }
		} = yield call(
			userApi.resetPassword,
			currentPassword,
			newPassword,
			retryNewPassword
		);

		if (code !== 'OK') {
			if (message.indexOf('old password') !== -1) {
				validationErrors.currentPassword = `${message.replace(
					'old',
					'Current'
				)}.`;
			}
			throw new ResetPasswordError(validationErrors);
		}
		yield put(actions.resetPasswordSuccess(data));
		alert(
			'For your security, please reauthenticate using your new password. Thank you!'
		);
		yield put(actions.logoutUserRequest());
	} catch (error) {
		yield put(actions.resetPasswordFailure(error));
	}
}

export function* adminResetPasswordSaga({ payload }: UserAction): SagaIterator {
	try {
		const validationErrors: ResetPassword = {};
		const lowercase = /[a-z]/;
		const uppercase = /[A-Z]/;
		const digit = /[0-9]/;
		const specialChar = /[\W]/;

		const email = String(payload.email).trim();

		const newPW = String(payload.newPassword).trim();

		const confirmPW = String(payload.confirmNewPassword).trim();

		const errorArray = [];

		if (newPW.length === 0) {
			validationErrors.newPasswordEmpty =
				'New password can not be empty.';
		}

		if (newPW.length < 6) {
			validationErrors.newPasswordLength =
				'New password must contain at least 6 characters.';
		}

		if (newPW !== confirmPW) {
			errorArray.push('match');
			validationErrors.retryNewPasswordMatch =
				'Repeat password and new password must match.';
		}
		if (!lowercase.test(newPW)) {
			errorArray.push('lower');
			validationErrors.newPasswordLower =
				'New password must contain at least one lowercase letter.';
		}
		if (!uppercase.test(newPW)) {
			errorArray.push('upper');
			validationErrors.newPasswordUpper =
				'New password must contain at least one uppercase letter.';
		}
		if (!digit.test(newPW)) {
			errorArray.push('digit');
			validationErrors.newPasswordNumber =
				'New password must contain at least one number.';
		}
		if (!specialChar.test(newPW)) {
			errorArray.push('special');
			validationErrors.newPasswordSpecial =
				'New password must contain at least one special character. (!,@,#,$,%,^,&,*)';
		}

		if (Object.keys(validationErrors).length > 0) {
			const errorString = [];
			for (const [key, value] of Object.entries(validationErrors)) {
				if (
					key != 'retryNewPasswordMatch' &&
					key != 'retryNewPassword' &&
					key != 'retryNewPasswordEmpty' &&
					key != 'currentPassword'
				) {
					errorString.push(value);
				}
			}
			validationErrors.newPassword = errorString.join('\n');

			throw new ResetPasswordError(validationErrors);
		}

		const {
			data: { code, message, data }
		} = yield call(userApi.adminResetPassword, email, newPW, confirmPW);

		if (code !== 'OK') {
			if (message.indexOf('old password') !== -1) {
				validationErrors.currentPassword = `${message.replace(
					'old',
					'Current'
				)}.`;
			}
			throw new ResetPasswordError(validationErrors);
		}
		alert('Password change successfull');
		yield put(actions.adminResetPasswordSuccess(data));
	} catch (error) {
		yield put(actions.adminResetPasswordFailure(error));
	}
}

export function* getUserSaga(): SagaIterator {
	try {
		const {
			data: { data }
		} = yield call(userApi.getUser);
		yield put(actions.userGetUserSuccess(data));
	} catch (error) {
		yield put(actions.userGetUserFailure(error));
	}
}

export function* createUserAccountsSaga({ payload }: any): SagaIterator {
	try {
		const response = yield call(userApi.adminCreateUsers, payload);
		const { status, data } = response;
		if (status !== 200) {
			if (typeof data.data === 'string') {
				throw new Error(data.data);
			} else {
				throw new Error(data.data.message);
			}
		}

		yield put(actions.createUserAccountsSuccess(data.data));
	} catch (error) {
		yield put(actions.createUserAccountsFailure(error));
	}
}

export function* getUserByEmailSaga({ payload }: any): SagaIterator {
	try {
		const {
			data: { data }
		} = yield call(userApi.getUserByEmail, payload);
		if (data.mailJetKeyCount) {
			data.user.mailJetKeyCount = data.mailJetKeyCount;
		}
		if (data.mailJetStatus) {
			data.user.mailJetStatus = data.mailJetStatus;
		}
		if (data.emailKeyName) {
			data.user.emailKeyName = data.emailKeyName;
		}
		if (data.emailSendingMethod) {
			data.user.initialEmailSendingMethod = data.emailSendingMethod;
		}
		if (data.user === null || Object.keys(data).length === 0) {
			yield put(actions.userGetUserByEmailNoUser());
		}
		if (data.message == 'no-user') {
			throw new Error('no-user');
		}
		yield put(actions.userGetUserByEmailSuccess(data.user));
	} catch (error) {
		yield put(actions.userGetUserByEmailFailure(error));
	}
}

export function* getUsersSaga(): SagaIterator {
	try {
		const {
			data: { data }
		} = yield call(userApi.getUsers);

		yield put(actions.userGetUsersSuccess(data));
	} catch (error) {
		yield put(actions.userGetUsersFailure(error));
	}
}

export function* getUpdateUserAdminSaga({ payload }): SagaIterator {
	let response;
	try {
		const { data } = yield call(userApi.updateUserAdmin, payload);
		if (data.mailJetStatus) {
			data.user.mailJetStatus = data.mailJetStatus;
		}
		if (data) {
			response = data;
		}
		yield put(actions.userUpdateUserAdminSuccess(data.user));
	} catch (error) {
		yield put(actions.userUpdateUserAdminFailure(response));
	}
}

export function* updateUserEmailsLimitSaga(): SagaIterator {
	try {
		yield call(userApi.updateUserEmailsLimit);

		yield put(actions.userUpdateEmailsLimitSuccess());
	} catch (error) {
		yield put(actions.userUpdateEmailsLimitFailure(error));
	}
}

export function* userAddEmailTokenSaga({ payload }): any {
	try {
		const { data, status } = yield call(userApi.userAddEmailToken, payload);

		if (status === 200 || status === '200') {
			yield put(actions.userAddEmailTokenSuccess(data));
		} else if (
			status === 400 &&
			data?.data?.error === 'Invalid state token'
		) {
			yield put(actions.userAddEmailTokenFailure('invalid'));
		} else if (
			status === 400 &&
			data?.data?.error === 'State token expired'
		) {
			yield put(actions.userAddEmailTokenFailure('expired'));
		}
	} catch (err) {
		throw new Error(err);
	}
}

export function* userAddNewUserRequest({ payload }): any {
	try {
		yield put(actions.userAddNewUserLoading());
		const { data, status } = yield call(
			userApi.userAddNewUserRequest,
			payload
		);
		if (status === 200) {
			yield put(actions.userAddNewUserSuccess(data));
		} else {
			alert('There was a problem sending your request, please try again');
		}
	} catch (err) {
		throw new Error(err);
	}
}

export function* userAddNewBccRequest({ payload }): any {
	try {
		yield put(actions.userAddNewBccLoading());
		const { data, status } = yield call(
			userApi.userAddNewBccRequest,
			payload
		);
		if (status === 200) {
			yield put(actions.userAddNewBccSuccess(data.user));
		} else {
			alert('There was a problem sending your request, please try again');
		}
	} catch (err) {
		yield put(actions.userAddNewBccFailure());
		throw new Error(err);
	}
}

export function* userDeleteBccRequest(): any {
	try {
		yield put(actions.userAddNewBccLoading());
		const { data, status } = yield call(userApi.userDeleteBccRequest);
		if (status === 200) {
			yield put(actions.userAddNewBccSuccess(data.user));
		} else {
			alert('There was a problem sending your request, please try again');
		}
	} catch (err) {
		yield put(actions.userAddNewBccFailure());
		throw new Error(err);
	}
}

export function* updateUserSubscription({ payload }): any {
	try {
		const { data } = yield call(
			userApi.updateSubscriptions,
			{
				direction: payload.direction,
				subscription: payload.subscription
			},
			payload.token
		);
	} catch (err) {
		throw new Error(err);
		alert(
			'There was a problem updating your subscription. Please reach out at support@re2.ai for help.'
		);
	}
}

export function* userDeleteUserRequest(action): any {
	try {
		const { data } = yield call(userApi.userDeleteUserRequest, {
			userId: action.payload
		});
		yield put(actions.deleteUserSuccess());
	} catch (err) {
		throw new Error(err);
		alert(
			'There was a problem updating your subscription. Please reach out at support@re2.ai for help.'
		);
	}
}

export function* userAddHostedEmail(action): any {
	try {
		const { data, status } = yield call(userApi.addHostedEmail, {
			userEmail: action.payload.userEmail,
			hostedEmail: action.payload.re2Email,
			nylasCode: action.payload.nylasCode
		});
		if (status === 200) {
			alert('success!');
		} else {
			throw new Error(data.data.message);
		}
	} catch (err) {
		alert(err);
	}
}

export function* userCreateScheduleRequest(): any {
	try {
		const { data } = yield call(userApi.userCreateScheduleRequest, {});
		if (data.data?.error === 'invalid_access_tokens') {
			yield put(actions.userCreateScheduleCalAuth());
		}
		yield put(actions.userCreateScheduleSuccess(data.data));
	} catch (err) {
		throw new Error(err);
		alert(
			'There was a problem updating your subscription. Please reach out at support@re2.ai for help.'
		);
	}
}

export function* adminAccessAccountSaga({ payload }: UserAction): SagaIterator {
	try {
		const res = yield call(userApi.adminAccessAccountRequest, {
			email: payload.email
		});

		const { data: adminTokenEncryptData } =
			yield call(getAdminTokenEncrypt);
		const adminTokenEncryptKey =
			adminTokenEncryptData?.data?.adminTokenEncryptAPIKey;

		if (res.status !== 200) {
			yield put(actions.adminAccessAccountFailure(res.data.data));
			return;
		}

		const encryptedToken = CryptoJS.AES.encrypt(
			JSON.stringify(res.data.data.accessToken),
			adminTokenEncryptKey
		).toString();
		const url = `${window.location.origin}/?admin=${encryptedToken}`;
		window.open(url, '_blank');
	} catch (err) {
		throw new Error(err);
	}
}

export function* userLogoutRequest(): any {
	try {
		const refreshToken = getUserRefreshToken();
		const { data } = yield call(userApi.userLogoutUser, { refreshToken });
		yield put(actions.logoutUserSuccess());
	} catch (err) {
		throw new Error(err);
	}
}

export function* getUserNewCommunications(): any {
	try {
		const { data } = yield call(userApi.userGetNewCommunications, {});
		yield put(actions.userGetNewCommunicationsSuccess(data.data));
	} catch (err) {
		throw new Error(err);
	}
}

export function* getUserNotifications(): any {
	try {
		const {
			data: {
				data: { notifications }
			}
		} = yield call(userApi.userGetNotifications, {});
		yield put(actions.userGetNotificationsSuccess(notifications));
	} catch (err) {
		throw new Error(err);
	}
}

export function* checkUserExists({ payload }: UserAction): SagaIterator {
	try {
		const response = yield call(userApi.checkUserExists, payload);

		yield put(
			actions.userCheckUserExistsSuccess(response.data.data.userExists)
		);
	} catch (err) {
		throw new Error(err);
	}
}

export function* postUserNotificationsRead(): any {
	try {
		const response = yield call(userApi.userPostNotificationsRead, {});
	} catch (err) {
		throw new Error(err);
	}
}

export function* signUpFacebookFreemium({ payload }: UserAction): SagaIterator {
	try {
		const response = yield call(userApi.signUpFacebookFreemium, payload);
	} catch (err) {
		throw new Error(err);
	}
}

export function* verifySignupEmail(token: UserAction): SagaIterator {
	try {
		const result = yield call(userApi.verifySignupEmail, token);
	} catch (error) {}
}

export function* generateOtpSecret(): SagaIterator {
	try {
		const response = yield call(userApi.generateOtpSecret);

		if (response.status !== 200) {
			yield put(actions.userGenerateOtpSecretFailure());
			return;
		}

		yield put(actions.userGenerateOtpSecretSuccess(response.data));
	} catch (err) {
		yield put(actions.userGenerateOtpSecretFailure());
	}
}

export function* enableOtp({ payload }: UserAction): SagaIterator {
	try {
		const response = yield call(userApi.enableOtp, payload);

		if (response.status !== 200) {
			yield put(actions.userEnableOtpFailure());
			return;
		}

		yield put(actions.userEnableOtpSuccess(response.data.recoveryCodes));
	} catch (err) {
		yield put(actions.userEnableOtpFailure());
	}
}

export function* deactivateOtp({ payload }: UserAction): SagaIterator {
	try {
		const response = yield call(userApi.deactivateOtp);

		if (response.status !== 200) {
			yield put(actions.userDeactivateOtpFailure());
		}

		yield put(actions.userDeactivateOtpSuccess());
	} catch (err) {
		yield put(actions.userDeactivateOtpFailure());
	}
}

export function* verifyOtp({ payload }: UserAction): SagaIterator {
	try {
		let response;
		try {
			response = yield call(userApi.verifyOtp, payload);
		} catch (err: any) {
			const errorData = err?.response?.data?.data || 'Unknown error';
			yield put(actions.userVerifyOtpFailure(errorData));
			return;
		}

		const {
			data: {
				data: {
					accessToken,
					refreshToken,
					user: {
						name,
						role,
						email,
						company,
						login_count: loginCount,
						facebookFreemium
					}
				} = {
					accessToken: undefined,
					refreshToken: undefined,
					user: {
						name: '',
						role: '',
						email: '',
						company: {
							name: ''
						},
						login_count: 0,
						facebookFreemium: undefined
					}
				}
			}
		} = response;

		yield put(
			actions.loginUserSuccess({
				accessToken,
				refreshToken,
				name,
				role,
				company,
				email,
				login_count: loginCount,
				facebookFreemium
			})
		);

		sendAnalytics({
			type: 'track',
			userId: email,
			eventName: 'User logged in'
		});
	} catch (err) {
		yield put(actions.userVerifyOtpFailure(''));
	}
}

export function* resetOtp({ payload }: UserAction): SagaIterator {
	try {
		const response = yield call(userApi.resetOtp, payload);

		if (response.status !== 200) {
			yield put(actions.userResetOtpFailure(response.data.data));
			return;
		}

		const {
			data: {
				data: {
					accessToken,
					refreshToken,
					user: {
						name,
						role,
						email,
						company,
						login_count: loginCount,
						facebookFreemium
					}
				} = {
					accessToken: undefined,
					refreshToken: undefined,
					user: {
						name: '',
						role: '',
						email: '',
						company: {
							name: ''
						},
						login_count: 0,
						facebookFreemium: undefined
					}
				}
			}
		} = response;

		yield put(
			actions.loginUserSuccess({
				accessToken,
				refreshToken,
				name,
				role,
				company,
				email,
				login_count: loginCount,
				facebookFreemium
			})
		);

		sendAnalytics({
			type: 'track',
			userId: email,
			eventName: 'User logged in'
		});
	} catch (err) {
		yield put(actions.userResetOtpFailure(''));
	}
}

export function* updateEmailSendingMethod({
	payload
}: {
	payload: { userEmail: string; emailSystem: string };
}): SagaIterator {
	try {
		const response = yield call(
			userApi.updateEmailSendingMethod,
			payload.userEmail,
			payload.emailSystem
		);
		if (response.status !== 200) {
			alert(response.data.message);
			return;
		}
		alert(response.data.data.message);
	} catch (error) {
		// TODO: Proper error handling
		alert(error);
	}
}

export function* updateUserActiveStatus({
	payload
}: {
	payload: { userEmail: string; action: string };
}): SagaIterator {
	try {
		const response = yield call(
			userApi.userUpdateActiveStatus,
			payload.userEmail,
			payload.action
		);
		if (response.status !== 200) {
			alert(response.data.message);
			return;
		}
		alert(response.data.data);
	} catch (error) {
		// TODO: Proper error handling
		alert(error);
	}
}
